import { createClient } from 'contentful';
import { base64Encode } from '@tuic/lib/helpers/string';
import {
  CF_DELIVERY_TOKEN_ACCOUNT,
  CF_DELIVERY_TOKEN_DASHBOARD,
  CF_DELIVERY_TOKEN_PDM,
  CF_DELIVERY_TOKEN_LIGHTHOUSE,
  CF_DELIVERY_TOKEN_SHARED,
  CF_DELIVERY_TOKEN_WEBSITE,
  CF_PREVIEW_TOKEN_ACCOUNT,
  CF_PREVIEW_TOKEN_DASHBOARD,
  CF_PREVIEW_TOKEN_PDM,
  CF_PREVIEW_TOKEN_LIGHTHOUSE,
  CF_PREVIEW_TOKEN_SHARED,
  CF_PREVIEW_TOKEN_WEBSITE,
  CF_ENV_ACCOUNT,
  CF_ENV_DASHBOARD,
  CF_ENV_SHARED,
  CF_ENV_WEBSITE,
  CF_ENV_PDM,
  CF_ENV_LIGHTHOUSE,
  CF_SPACE_ID_ACCOUNT,
  CF_SPACE_ID_DASHBOARD,
  CF_SPACE_ID_PDM,
  CF_SPACE_ID_LIGHTHOUSE,
  CF_SPACE_ID_SHARED,
  CF_SPACE_ID_WEBSITE,
  CF_SPACE_ID_CORPORATE,
  CF_ENV_CORPORATE,
  CF_PREVIEW_TOKEN_CORPORATE,
  CF_DELIVERY_TOKEN_CORPORATE,
} from './config';
import type { CreateClientParams } from 'contentful';
import { getSentry } from './sentry';
type CaptureContext = any;

export type SPACE_NAME =
  | 'account'
  | 'dashboard'
  | 'shared'
  | 'pdm'
  | 'lighthouse'
  | 'website'
  | 'corporate';

type ClientCreationOptions = {
  preview?: boolean;
};

type SpaceClientCreationOptions = ClientCreationOptions & {
  space: SPACE_NAME;
};

/**
 * Returns a configured client based on given space name.
 */
export const createConfiguredClientBySpace = ({
  space,
  preview,
}: SpaceClientCreationOptions) => {
  if (space === 'account') {
    return createConfiguredAccountClient({ preview });
  }

  if (space === 'dashboard') {
    return createConfiguredDashboardClient({ preview });
  }

  if (space === 'shared') {
    return createConfiguredSharedClient({ preview });
  }

  if (space === 'pdm') {
    return createConfiguredPdmClient({ preview });
  }

  if (space === 'lighthouse') {
    return createConfiguredLighthouseClient({ preview });
  }

  if (space === 'website') {
    return createConfiguredWebsiteClient({ preview });
  }

  if (space === 'corporate') {
    return createConfiguredCorporateClient({ preview });
  }

  throw new Error(`No client found for space "${space}".`);
};

export const createConfiguredSharedClient = ({
  preview,
}: ClientCreationOptions | undefined = {}) => {
  return createConfiguredRestClient(
    CF_SPACE_ID_SHARED,
    CF_ENV_SHARED,
    preview ? CF_PREVIEW_TOKEN_SHARED : CF_DELIVERY_TOKEN_SHARED,
    !!preview,
  );
};

export const createConfiguredWebsiteClient = ({
  preview,
}: ClientCreationOptions | undefined = {}) => {
  return createConfiguredRestClient(
    CF_SPACE_ID_WEBSITE,
    CF_ENV_WEBSITE,
    preview ? CF_PREVIEW_TOKEN_WEBSITE : CF_DELIVERY_TOKEN_WEBSITE,
    !!preview,
  );
};

export const createConfiguredPdmClient = ({
  preview,
}: ClientCreationOptions | undefined = {}) => {
  return createConfiguredRestClient(
    CF_SPACE_ID_PDM,
    CF_ENV_PDM,
    preview ? CF_PREVIEW_TOKEN_PDM : CF_DELIVERY_TOKEN_PDM,
    !!preview,
  );
};

export const createConfiguredLighthouseClient = ({
  preview,
}: ClientCreationOptions | undefined = {}) => {
  return createConfiguredRestClient(
    CF_SPACE_ID_LIGHTHOUSE,
    CF_ENV_LIGHTHOUSE,
    preview ? CF_PREVIEW_TOKEN_LIGHTHOUSE : CF_DELIVERY_TOKEN_LIGHTHOUSE,
    !!preview,
  );
};

export const createConfiguredAccountClient = ({
  preview,
}: ClientCreationOptions | undefined = {}) => {
  return createConfiguredRestClient(
    CF_SPACE_ID_ACCOUNT,
    CF_ENV_ACCOUNT,
    preview ? CF_PREVIEW_TOKEN_ACCOUNT : CF_DELIVERY_TOKEN_ACCOUNT,
    !!preview,
  );
};

export const createConfiguredDashboardClient = ({
  preview,
}: ClientCreationOptions | undefined = {}) => {
  return createConfiguredRestClient(
    CF_SPACE_ID_DASHBOARD,
    CF_ENV_DASHBOARD,
    preview ? CF_PREVIEW_TOKEN_DASHBOARD : CF_DELIVERY_TOKEN_DASHBOARD,
    !!preview,
  );
};

export const createConfiguredCorporateClient = ({
  preview,
}: ClientCreationOptions | undefined = {}) => {
  return createConfiguredRestClient(
    CF_SPACE_ID_CORPORATE,
    CF_ENV_CORPORATE,
    preview ? CF_PREVIEW_TOKEN_CORPORATE : CF_DELIVERY_TOKEN_CORPORATE,
    !!preview,
  );
};

const createConfiguredRestClient = (
  space: string,
  environment: string,
  accessToken: string,
  preview: boolean,
) => {
  return createClient({
    space,
    environment,
    accessToken,
    host: preview ? 'preview.contentful.com' : undefined,
    headers: {
      'x-contentful-resource-resolution': base64Encode(
        JSON.stringify(getExtraTokens(space, preview)),
      ),
    },
    responseLogger: logResponse,
  });
};

type ResponseFormat = {
  config?: {
    method?: string;
    url?: string;
    params?: Record<string, any>;
  };
  status?: number;
  response?: {
    status?: number;
  };
};

const logResponse: CreateClientParams['responseLogger'] = response => {
  const sentry = getSentry();

  const normalized = response as ResponseFormat;

  const status = normalized?.status ?? normalized?.response?.status ?? 0;
  if (status < 300) return;
  if (status === 404) return;

  if (response instanceof Error) {
    const context: CaptureContext = {
      level: 'error',
      tags: {
        contentful: true,
      },
      extra: {
        status,
        method: normalized?.config?.method ?? 'unknown',
        url: normalized?.config?.url ?? 'unknown',
        query: normalized?.config?.params ?? {},
      },
    };
    sentry?.captureMessage('Contentful Request failed', context);
  }
};

const getExtraTokens = (originSpaceId: string, preview: boolean) => {
  const spaceConfig: [boolean, string, string, string][] = [
    // [
    //   originSpaceId !== CF_SPACE_ID_ACCOUNT,
    //   CF_SPACE_ID_ACCOUNT,
    //   CF_DELIVERY_TOKEN_ACCOUNT,
    //   CF_PREVIEW_TOKEN_ACCOUNT,
    // ],
    // [
    //   originSpaceId !== CF_SPACE_ID_DASHBOARD,
    //   CF_SPACE_ID_DASHBOARD,
    //   CF_DELIVERY_TOKEN_DASHBOARD,
    //   CF_PREVIEW_TOKEN_DASHBOARD,
    // ],
    [
      originSpaceId !== CF_SPACE_ID_SHARED,
      CF_SPACE_ID_SHARED,
      CF_DELIVERY_TOKEN_SHARED,
      CF_PREVIEW_TOKEN_SHARED,
    ],
    // [
    //   originSpaceId !== CF_SPACE_ID_WEBSITE,
    //   CF_SPACE_ID_WEBSITE,
    //   CF_DELIVERY_TOKEN_WEBSITE,
    //   CF_PREVIEW_TOKEN_WEBSITE,
    // ],
    [
      originSpaceId !== CF_SPACE_ID_PDM,
      CF_SPACE_ID_PDM,
      CF_DELIVERY_TOKEN_PDM,
      CF_PREVIEW_TOKEN_PDM,
    ],
  ];

  const spaces: Record<string, string> = {};

  spaceConfig.forEach(([include, spaceId, token, previewToken]) => {
    if (!include) {
      return;
    }

    spaces[spaceId] = preview ? previewToken : token;
  });

  return {
    spaces,
  };
};
